/**
 * Custom hook to auto-dismiss messages after a specified duration
 * Provides auto-scrolling to top when message appears
 */

import { useEffect } from "react";

interface UseMessageAutoDismissOptions {
  /** Message to watch for changes */
  message: string | null;
  /** Callback to clear the message */
  onDismiss: () => void;
  /** Duration in milliseconds before auto-dismiss (default: 5000) */
  duration?: number;
  /** Whether to scroll to top when message appears (default: true) */
  scrollToTop?: boolean;
}

/**
 * Hook to auto-dismiss messages and optionally scroll to top
 * 
 * @param options - Configuration options
 * 
 * @example
 * ```tsx
 * useMessageAutoDismiss({
 *   message: successMessage,
 *   onDismiss: () => dispatch(setSuccessMessage(null)),
 *   duration: 5000,
 * });
 * ```
 */
export const useMessageAutoDismiss = ({
  message,
  onDismiss,
  duration = 5000,
  scrollToTop = true,
}: UseMessageAutoDismissOptions): void => {
  // Auto-dismiss after specified duration
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        onDismiss();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [message, onDismiss, duration]);

  // Scroll to top when message appears
  useEffect(() => {
    if (message && scrollToTop) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [message, scrollToTop]);
};
